import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { OktaService } from './okta.service';
import { environment } from '../../../environments/environment';
import { HcToastOptions, HcToasterService } from '@healthcatalyst/cashmere';

export interface DataSetElement {
    'dataSetID': number,
    'name': string,
    'references': number
}

@Injectable()
export class DataSetService {
    datasetList:BehaviorSubject<DataSetElement[] | null> = new BehaviorSubject<DataSetElement[] | null>(null);
    loadFailed = false;

    constructor( private http: HttpClient, private oktaService: OktaService, private toasterService: HcToasterService ){};

    get datasetsUpdated(): Observable<DataSetElement[] | null> {
        return this.datasetList.asObservable();
    }

    refresh() {
        this.loadFailed = false;

        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = {headers: headers};

        this.http.get<any>( environment.azure.url + '/admin.datasets', options)
            .subscribe({
                next: data => {
                    let loadedDatasets: DataSetElement[] = [];
                    data.forEach((dataset:any) => {
                        loadedDatasets.push({
                            dataSetID: dataset.dataSetID,
                            name: dataset.name,
                            references: dataset.references
                        });
                    });
                    this.datasetList.next ( loadedDatasets );
                    this.loadFailed = false;
                },
                error: msg => { 
                    this.loadFailed = true;
                    this.datasetList.next(null);
                    console.log( msg );
                }
            });
    }

    add( target: DataSetElement ) {
        // Write target to database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = {headers: headers};

        let postSub = this.http.post<any>( environment.azure.url + '/admin.datasets.add',
            {
                name: target.name
            },
            options
        )
        .subscribe({
            next: () => {
                this.refresh();
                const options: HcToastOptions = {
                    header: 'Data Set Added',
                    body: target.name + " successfully added.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Add Failed',
                    body: "Unable to add data set. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log( msg );
                postSub.unsubscribe();
            }
        });
    }

    remove( target: DataSetElement ) {
        // Remove target from database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = {headers: headers};

        let postSub = this.http.post<any>( environment.azure.url + '/admin.datasets.remove',
            {
                dataSetID: target.dataSetID
            },
            options
        )
        .subscribe({
            next: () => {
                this.refresh();
                const options: HcToastOptions = {
                    header: 'Data Set Removed',
                    body: target.name + " successfully removed.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Remove Failed',
                    body: "Unable to remove data set. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log( msg );
                postSub.unsubscribe();
            }
        });
    }

    update( target: DataSetElement ) {
        // Update target in database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = {headers: headers};

        let postSub = this.http.post<any>( environment.azure.url + '/admin.datasets.update',
            {
                dataSetID: target.dataSetID,
                name: target.name
            },
            options
        )
        .subscribe({
            next: () => {
                this.refresh();
                const options: HcToastOptions = {
                    header: 'Data Set Updated',
                    body: target.name + " successfully updated.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Update Failed',
                    body: "Unable to update data set. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log( msg );
                postSub.unsubscribe();
            }
        });
    }
}
